/**
 * The evaluation loop's fixtures (Navy Phase 0 handoff "The
 * evaluation loop" and "Fixture notes"): the one recording, its transcript,
 * the reference key's contents and one authored comparison per scenario.
 *
 * Nothing is transcribed, parsed or compared in Phase 0. Every audio-built
 * assessment plays the one recording, and the transcript and every evidence
 * time are authored against it. The transcript is shared, so the words do not
 * change between scenarios; only the outcome against each item does.
 */

/**
 * The recording an audio-built assessment plays. None ships with the Console:
 * a recording belongs behind sign-in, so the player has no source until the
 * Assessments API serves each assessment's own. The transcript, its times and
 * the comparison still show.
 */
export const AUDIO_SRC = null;
/** The authored recording's length, which the transcript's times are written against. */
export const AUDIO_SEC = 89;

/* Waveform peaks for that recording, 700 buckets, carried over from the
   prototype so the wave draws without decoding the file. */
const WAVE_PACKED = '4,1109,867,820,340,790,655,483,319,212,900,328,406,581,815,115,716,1161,834,554,1222,674,818,1107,764,679,1016,94,542,496,479,1352,1148,583,35,45,37,12,259,1210,678,785,225,1241,653,219,220,628,681,740,429,456,713,174,120,35,13,16,28,1367,1341,164,787,275,844,582,566,764,484,958,543,58,22,418,103,930,696,281,24,783,921,764,409,305,251,1016,224,649,924,950,249,10,12,13,15,25,1124,248,856,549,363,589,391,622,395,19,19,18,16,13,158,1335,648,1398,1142,627,14,14,147,868,804,561,495,1028,418,265,1328,1018,280,931,815,660,1027,639,305,1126,1201,359,21,24,16,22,840,532,623,657,607,109,11,14,15,175,1214,839,355,496,562,587,808,562,409,268,776,1041,229,143,237,1143,613,161,1045,1165,140,27,659,1108,653,689,849,184,56,13,14,12,382,834,914,165,779,693,179,46,695,558,289,381,469,203,490,464,669,285,844,116,124,14,15,14,501,542,716,740,1440,836,127,472,775,948,610,875,765,664,499,1015,346,9,422,608,585,566,310,151,987,340,890,385,804,1150,173,869,745,483,559,720,300,972,634,83,29,21,535,309,380,590,574,788,831,198,722,316,804,404,533,770,516,899,714,301,35,12,942,929,1246,772,406,853,948,694,223,1363,984,375,8,13,9,750,422,466,162,490,116,196,666,474,174,11,11,603,1350,544,535,432,144,378,512,341,440,337,138,649,1009,538,1060,680,135,191,753,617,830,1036,467,9,17,14,11,9,710,665,596,483,767,507,10,557,297,733,919,1031,728,485,402,742,809,207,1476,707,13,548,1262,556,59,651,168,289,729,848,561,1351,811,496,691,709,942,552,104,23,17,7,149,843,90,487,444,264,289,342,320,210,299,211,89,21,6,10,104,943,604,585,939,134,896,58,979,257,397,854,783,621,336,598,761,950,808,30,23,11,209,1029,841,1130,604,310,189,1165,599,1064,689,786,185,966,783,1162,147,416,616,604,414,90,459,1329,598,647,840,947,195,645,1375,1021,286,11,13,10,788,683,677,191,882,424,116,11,10,138,1236,509,454,1359,666,110,751,905,848,540,948,231,1079,940,806,124,505,589,512,462,1017,880,637,43,135,1403,1079,260,254,750,1029,738,810,1206,219,19,19,19,23,645,785,1109,866,251,32,481,302,373,63,682,393,12,16,12,14,450,1277,1393,1663,2106,1551,252,19,7,8,809,568,1259,1014,1077,814,435,77,10,21,1137,1000,948,1010,1172,839,121,242,901,1098,921,695,353,394,200,1117,1372,445,24,1078,1060,525,930,1190,1427,1056,94,27,20,11,262,899,550,429,843,193,22,1286,787,1184,724,190,12,23,321,785,519,495,616,705,261,696,273,1018,774,37,12,313,526,838,798,573,673,473,757,400,823,25,16,32,28,37,663,668,1036,510,141,11,11,12,80,780,1453,504,182,984,1119,966,1032,617,19,9,586,683,961,1239,600,1296,1411,398,12,9,10,7,142,659,1008,722,448,151,574,158,139,12,11,10,37,996,325,350,378,106,1017,295,24,9,16,13,13,998,644,513,621,724,200,13,10,583,1544,766,175,87,599,1171,828,942,682,330,934,828,995,222,366,844,879,133,10,15,458,1136,765,1687,757,487,1139,660,900,529,89,11,13,11,11';
let wavePeaks = null;
export function getWavePeaks() {
  if (!wavePeaks) wavePeaks = WAVE_PACKED.split(',').map((v) => Number(v) / 10000);
  return wavePeaks;
}

/* The recording's transcript: timestamped segments, no speaker labels. A
   transcript-built assessment shows the same words with the times dropped. */
export const TRANSCRIPT = [
  { at: 0.0, text: 'Initiate the man overboard drill. Standing stern watch.' },
  { at: 3.7, text: 'Man overboard! Man overboard! Port side!' },
  { at: 6.4, text: 'BM1 Carter reacting. Reyes, toss the ring and the dan buoy.' },
  { at: 10.4, text: 'Keep pointing. Lyn, mark GPS now.' },
  { at: 12.7, text: 'Float and dan buoy away. MOB strobe deployed.' },
  { at: 15.7, text: 'I have visual. I am pointing.' },
  { at: 17.7, text: 'PO2 Lyn. MOB position marked on GPS. Coordinates logged.' },
  { at: 21.5, text: 'Radioing base. Halifax base, this is CG-236.' },
  { at: 24.5, text: 'This is a drill. Simulated man overboard.' },
  { at: 27.3, text: 'Position 44 degrees 39.8 north, 63 degrees 34.1 west.' },
  { at: 30.9, text: 'Recovery under way. Over to Halifax base radio.' },
  { at: 34.1, text: 'Roger CG-236. Drill acknowledged. Standing by.' },
  { at: 37.2, text: 'BM1 Carter, executing Williamson turn to port.' },
  { at: 40.3, text: 'Prep the recovery pole and the heaving line.' },
  { at: 43.3, text: 'Brief change in engine RPM. ENS Hall.' },
  { at: 45.8, text: 'Deck crew, confirm victim visual.' },
  { at: 48.0, text: 'Seaman Reyes, still visual, strobe light blinking.' },
  { at: 51.4, text: "Eleven o'clock off the bow. BM1 Carter approaching target." },
  { at: 55.4, text: 'Coming alongside slowly. Engines to idle.' },
  { at: 58.1, text: 'Reyes, recover on your mark.' },
  { at: 60.0, text: 'Seaman Reyes reaches with the pole. Hold. Got the line on him.' },
  { at: 64.3, text: 'Pulling in now. Simulated victim secure.' },
  { at: 67.0, text: 'Bringing him to the rail. ENS Hall, well done.' },
  { at: 70.1, text: 'Assume victim is hypothermic. Perform verbal contact check.' },
  { at: 74.1, text: 'Seaman Reyes. Sir, can you hear me? We are bringing you aboard.' },
  { at: 78.4, text: 'Simulated response, conscious but cold. BM1 Carter.' },
  { at: 81.8, text: 'Reyes, simulate the sling hoist if needed.' },
  { at: 84.7, text: 'Get them aboard into the warm zone. End of drill.' },
];

/* The reference key's contents, by Library document id, authored against the
   seeded Bridge Command Reference Key (d1). Its sections group the comparison. */
export const REF_KEYS = {
  d1: [
    {
      section: 'Watch handover',
      items: [
        { id: 'rk_01', text: 'Hand over the watch with a full situation brief' },
        { id: 'rk_02', text: 'Confirm position, course and speed at handover' },
        { id: 'rk_03', text: 'Confirm the passage plan in use' },
      ],
    },
    {
      section: 'Lookout and reporting',
      items: [
        { id: 'rk_04', text: 'Post a dedicated lookout' },
        { id: 'rk_05', text: 'Report every new contact to the officer of the watch' },
        { id: 'rk_06', text: 'Report contact bearing, range and aspect' },
        { id: 'rk_07', text: 'Call out any contact held on a steady bearing' },
      ],
    },
    {
      section: 'Manoeuvring',
      items: [
        { id: 'rk_08', text: 'State the intended manoeuvre before ordering it' },
        { id: 'rk_09', text: 'Give helm orders in the standard form' },
        { id: 'rk_10', text: 'Confirm the helm order is repeated back' },
        { id: 'rk_11', text: 'Check the rudder and engine response' },
        { id: 'rk_12', text: 'Return to the ordered course and report it' },
      ],
    },
    {
      section: 'Communications',
      items: [
        { id: 'rk_13', text: 'Make the required report to base on the working channel' },
        { id: 'rk_14', text: 'Open the report with the vessel call sign' },
        { id: 'rk_15', text: 'Pass position in latitude and longitude' },
        { id: 'rk_16', text: 'Identify the exercise as a drill' },
        { id: 'rk_17', text: 'Acknowledge the reply from base' },
      ],
    },
    {
      section: 'Emergency response',
      items: [
        { id: 'rk_18', text: 'Raise the alarm and state the side' },
        { id: 'rk_19', text: 'Mark the position on GPS immediately' },
        { id: 'rk_20', text: 'Deploy the lifebuoy and marker' },
        { id: 'rk_21', text: 'Keep a continuous visual on the casualty' },
        { id: 'rk_22', text: 'Execute the recovery turn' },
        { id: 'rk_23', text: 'Brief the deck party before the approach' },
        { id: 'rk_24', text: 'Bring the vessel alongside with the engines at idle' },
        { id: 'rk_25', text: 'Confirm the casualty is secure before recovery' },
        { id: 'rk_26', text: 'Carry out a verbal contact check with the casualty' },
      ],
    },
  ],
};

/* The transcript segment each item was found in. One map, because the one
   recording is shared; what changes per scenario is the outcome. The index is
   the segment's identity here (handoff questions.md, 10). */
const EVIDENCE = {
  rk_01: 0, rk_02: 9, rk_04: 0, rk_05: 1, rk_06: 17, rk_07: 5,
  rk_08: 12, rk_09: 12, rk_11: 14, rk_13: 7, rk_14: 7, rk_15: 9,
  rk_16: 8, rk_17: 11, rk_18: 1, rk_19: 6, rk_20: 4, rk_21: 16,
  rk_22: 12, rk_23: 13, rk_24: 18, rk_25: 21, rk_26: 23,
};

/* One plausible comparison per Library scenario, authored rather than
   generated. Anything not listed was found. An item with no evidence segment
   can only be not found, so those are listed in every scenario. */
export const COMPARISONS = {
  // Strait Transit - Fishing Season
  s1: {
    unclear: ['rk_11', 'rk_16', 'rk_21'],
    notFound: ['rk_03', 'rk_10', 'rk_12', 'rk_18', 'rk_19', 'rk_20', 'rk_22', 'rk_23', 'rk_24', 'rk_25', 'rk_26'],
  },
  // Restricted Visibility Approach
  s2: {
    unclear: ['rk_16', 'rk_23'],
    notFound: ['rk_03', 'rk_10', 'rk_12', 'rk_18', 'rk_19', 'rk_20', 'rk_22', 'rk_25', 'rk_26'],
  },
  // Man Overboard Recovery
  s3: {
    unclear: ['rk_02'],
    notFound: ['rk_03', 'rk_10', 'rk_12'],
  },
};

/* The three outcomes. Told apart by the mark in the box and by words, never
   by colour: nothing here is a pass or a fail, because accuracy is not
   evaluated in this phase. */
export const OUTCOMES = {
  found: { label: 'Found', mark: 'check' },
  unclear: { label: 'Unclear', mark: 'minus' },
  not_found: { label: 'Not found', mark: null },
};

/** The scenario's reference key document: its first, when it binds several. */
export function referenceKeyDoc(scenario, documents) {
  const id = scenario && scenario.referenceKeyIds && scenario.referenceKeyIds[0];
  return (id && (documents || []).find((d) => d.id === id)) || null;
}

/** The key's sections, in key order, or none when its contents aren't authored. */
export function referenceKeySections(scenario, documents) {
  const doc = referenceKeyDoc(scenario, documents);
  return (doc && REF_KEYS[doc.id]) || [];
}

/**
 * Every reference key item, grouped by section in key order, with its outcome
 * and its evidence: the segment and its index, or null when it was not found.
 */
export function buildComparison(scenario, documents) {
  const spec = (scenario && COMPARISONS[scenario.id]) || { unclear: [], notFound: [] };
  return referenceKeySections(scenario, documents).map((sec) => ({
    section: sec.section,
    items: sec.items.map((it) => {
      const segIndex = EVIDENCE[it.id];
      // No evidence segment means not found, whatever the scenario lists: a
      // scenario saved this session has no authored comparison, and must not
      // read as every item Found with no quote.
      let outcome = 'found';
      if (segIndex == null || spec.notFound.includes(it.id)) outcome = 'not_found';
      else if (spec.unclear.includes(it.id)) outcome = 'unclear';
      const hasEvidence = outcome !== 'not_found';
      return {
        id: it.id,
        text: it.text,
        outcome,
        segIndex: hasEvidence ? segIndex : null,
        segment: hasEvidence ? TRANSCRIPT[segIndex] : null,
      };
    }),
  }));
}

/** How many items were found, of how many: the count chip's "N of M items". */
export function comparisonCounts(groups) {
  const items = groups.flatMap((g) => g.items);
  return { found: items.filter((it) => it.outcome === 'found').length, total: items.length };
}

/** A playback position as m:ss: "0:27", "1:29". A duration, not a date. */
export function clock(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** The segment the playhead is in: the last one starting at or before it. */
export function segmentAt(sec) {
  let index = -1;
  TRANSCRIPT.forEach((seg, i) => { if (sec + 0.01 >= seg.at) index = i; });
  return index;
}

/**
 * The transcript as a body of text, three segments to a paragraph: without
 * speaker labels there is nothing truer to break on. Each passage keeps its
 * segment index so a comparison card can find it.
 */
export function transcriptParagraphs() {
  const out = [];
  TRANSCRIPT.forEach((seg, index) => {
    if (index % 3 === 0) out.push([]);
    out[out.length - 1].push({ index, text: seg.text });
  });
  return out;
}
